"use client"

import { useRef, useMemo } from "react"
import Image from "next/image"
import clsx from "clsx"
import { Gallery, Item } from "react-photoswipe-gallery"
import { DZTypes } from "@dealerzone/libs"
import "photoswipe/dist/photoswipe.css"
import "./photoswipe-custom.css"

const MAX_THUMBS = 35

type PswpItem = {
  src: string
  thumbnail: string
  w: number
  h: number
}

type Props = {
  vehicle: DZTypes.Vehicle
}

export default function VehicleImages({ vehicle }: Props) {
  const openFnRefs = useRef<((e: React.MouseEvent) => void)[]>([])

  const images = vehicle.images && vehicle.images.length > 0
    ? vehicle.images
    : [{ large: vehicle.main_image.large, medium: vehicle.main_image.medium, small: vehicle.main_image.small, is_main: true, large_size: { width: "800", height: "600" } }]

  const pswpItems: PswpItem[] = useMemo(() => {
    if (vehicle.pswp) {
      try {
        return JSON.parse(vehicle.pswp)
      } catch {
        return []
      }
    }
    return images.map((img) => ({
      src: img.large,
      thumbnail: img.small,
      w: parseInt(img.large_size?.width || "800"),
      h: parseInt(img.large_size?.height || "600"),
    }))
  }, [vehicle, images])

  function handleImageClick(index: number) {
    return (e: React.MouseEvent) => {
      if (openFnRefs.current[index]) {
        openFnRefs.current[index](e)
      }
    }
  }

  function thumbnails(buttonClass: string) {
    return (
      <>
        {pswpItems.slice(0, MAX_THUMBS).map((item, index) => (
          <button
            key={index}
            onClick={handleImageClick(index)}
            className={clsx("relative cursor-pointer overflow-hidden rounded-lg", buttonClass)}
          >
            <Image
              src={item.thumbnail}
              alt={`${vehicle.name} - Thumbnail ${index + 1}`}
              width={100}
              height={75}
              className="aspect-image w-full object-cover"
            />
          </button>
        ))}
        {pswpItems.length > MAX_THUMBS && (
          <button
            onClick={handleImageClick(MAX_THUMBS)}
            className={clsx("relative cursor-pointer overflow-hidden rounded-lg bg-gray-200", buttonClass)}
          >
            <div className="flex aspect-image items-center justify-center">
              <span className="text-sm font-medium text-gray-600">+{pswpItems.length - MAX_THUMBS}</span>
            </div>
          </button>
        )}
      </>
    )
  }

  return (
    <div className="min-w-0 space-y-4">
      <Gallery options={{ tapAction: "close" }}>
        <button
          onClick={handleImageClick(0)}
          className="relative block w-full cursor-pointer overflow-hidden rounded-xl bg-gray-100"
        >
          <Image
            src={pswpItems[0]?.src || images[0].large}
            alt={`${vehicle.name} - Main image`}
            width={800}
            height={600}
            className="aspect-image w-full object-cover"
            priority
          />
          {vehicle.is_clean && (
            <div className="absolute top-4 left-4 flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 shadow-lg">
              <i className="bootstrap-award-fill text-xl text-white" />
              <span className="text-lg font-bold text-white">Clean Title</span>
            </div>
          )}
          {vehicle.is_repaired && !vehicle.is_clean && (
            <div className="absolute top-4 left-4 flex items-center gap-2 rounded-lg bg-gradient-to-r from-amber-500 to-yellow-500 px-4 py-2 shadow-lg">
              <svg className="h-5 w-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              <span className="text-lg font-bold text-white">Ready To Go</span>
            </div>
          )}
        </button>

        <div className="hidden">
          {pswpItems.map((item, index) => (
            <Item
              key={index}
              original={item.src}
              thumbnail={item.thumbnail}
              width={item.w}
              height={item.h}
            >
              {({ ref, open }) => {
                openFnRefs.current[index] = open
                return <span ref={ref} />
              }}
            </Item>
          ))}
        </div>

        {pswpItems.length > 1 && (
          <>
            <div className="flex gap-2 overflow-x-auto pb-1 sm:hidden [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              {thumbnails("w-20 shrink-0")}
            </div>

            <div className="hidden gap-2 sm:grid sm:grid-cols-6 lg:grid-cols-5">
              {thumbnails("")}
            </div>
          </>
        )}
      </Gallery>
    </div>
  )
}
